import React from 'react';
import { Tag, Typography, theme } from 'antd';
import type { FtueLevelRow, FtueProgression } from '@/types';
import { fmtNum, fmtPct } from '../bq-insights/bqMath';
import { earlyLabel, fmtElapsed, fmtMinutes, partialBaseLabel, tooFew } from './ftueFormat';

const { Text } = Typography;

/** Level 2 unlocks Custom Match, Level 5 unlocks Blitz Showdown. */
const LEVELS = [2, 5];

interface Tile {
  key: string;
  label: string;
  value: string;
  /** Lines under the figure. */
  details: string[];
  /** Players the figure is measured over. */
  base: number;
}

type Kind = 'matches' | 'reach' | 'time';

function tile(kind: Kind, level: number, p: FtueProgression): Tile {
  const row: FtueLevelRow | undefined = p.levels.find((l) => l.level === level);
  const reached = row?.reached ?? 0;
  const key = `${kind}-${level}`;
  if (kind === 'matches') {
    const m = row?.matches ?? null;
    return {
      key,
      label: `Matches to Level ${level}`,
      value: m ? fmtNum(m.median) : '—',
      details: [m ? `middle half ${m.p25}–${m.p75}` : 'median'],
      base: reached,
    };
  }
  if (kind === 'reach') {
    return {
      key,
      label: `Reach Level ${level}`,
      value: fmtPct(row?.reachShare ?? null),
      details: ['of tutorial finishers'],
      base: p.base,
    };
  }
  const elapsed = row?.elapsedHours ?? null;
  return {
    key,
    label: `Time to Level ${level}`,
    value: fmtMinutes(row?.playMinutes ?? null),
    details: [
      'median play time',
      ...(elapsed === null ? [] : [`${fmtElapsed(elapsed)} after the tutorial`]),
    ],
    base: reached,
  };
}

export interface ProgressionTilesProps {
  progression: FtueProgression;
  minPlayers: number;
}

/** The six progression tiles: matches, reach and time to Level 2 and to Level 5. */
export const ProgressionTiles: React.FC<ProgressionTilesProps> = ({ progression, minPlayers }) => {
  const { token } = theme.useToken();
  const early = earlyLabel(progression);
  const partial = partialBaseLabel(progression);
  const tiles = (['matches', 'reach', 'time'] as const).flatMap((kind) =>
    LEVELS.map((level) => tile(kind, level, progression))
  );
  return (
    <div>
      {early !== null && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="orange">{early}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {`Not final: fewer than ${fmtNum(minPlayers)} finishers have had 7 days yet.`}
          </Text>
        </div>
      )}
      {partial !== null && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {partial}
          </Text>
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 10,
        }}
      >
        {tiles.map((t) => {
          const few = tooFew(t.base, minPlayers);
          return (
            <div key={t.key} data-tile={t.key}>
              <div
                style={{
                  background: token.colorFillQuaternary,
                  borderRadius: 10,
                  padding: '12px 14px',
                  height: '100%',
                }}
              >
                <div style={{ opacity: few ? 0.5 : 1 }}>
                  <Text type="secondary">{t.label}</Text>
                  <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2 }}>{t.value}</div>
                  {t.details.map((d) => (
                    <div key={d} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                      {d}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: token.colorTextTertiary, marginTop: 4 }}>
                  {`n = ${fmtNum(t.base)}`}
                  {few && (
                    <Text type="warning" style={{ fontSize: 11, marginLeft: 6 }}>
                      too few players
                    </Text>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
